import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { apiService } from '../../services/api';
import { nextRequestToken, isStaleRequest, loadStart, loadSettle, dateUtils } from './shared';

export const createFinancialsSlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set, get) => ({
  // Project Management with API integration
  fetchProjects: async (condoId?: string) => {
    const token = nextRequestToken('projects');
    set(loadStart('projects')); // 106.1
    try {
      const projects = await apiService.getProjects(condoId);
      if (isStaleRequest(token)) return; // 94.4 — superseded by a newer fetch
      set(state => ({ ...loadSettle('projects', null)(state), projects }));
    } catch (error: any) {
      if (isStaleRequest(token)) return;
      set(loadSettle('projects', error.message || 'Failed to fetch projects'));
    }
  },

  // Sprint 151.1 follow-up (ADR-147) — the local-only addProject/updateProject/
  // deleteProject mutators were deleted. They mutated the store WITHOUT calling
  // the backend, which is exactly how the Projects page silently discarded every
  // edit (design-review #1). The API-backed variants below are the only write
  // path; reads come through fetchProjects.

  createProject: async (projectData) => {
    const { setLoading, setError, fetchProjects } = get();
    setLoading(true);
    try {
      await apiService.createProject(projectData);
      await fetchProjects(projectData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  },

  updateProjectApi: async (id, projectData) => {
    const { setLoading, setError, fetchProjects } = get();
    setLoading(true);
    try {
      await apiService.updateProject(id, projectData);
      await fetchProjects(projectData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update project');
    } finally {
      setLoading(false);
    }
  },

  deleteProjectApi: async (id) => {
    const { setLoading, setError, fetchProjects } = get();
    setLoading(true);
    try {
      await apiService.deleteProject(id);
      await fetchProjects();
    } catch (error: any) {
      setError(error.message || 'Failed to delete project');
    } finally {
      setLoading(false);
    }
  },

  getProjectById: (id) =>
    get().projects.find(project => project.id === id),

  getProjectsByCondo: (condoId) =>
    get().projects.filter(project => project.condoId === condoId),

  getProjectsByStatus: (status, condoId) => {
    const { projects } = get();
    const filteredProjects = condoId ? projects.filter(p => p.condoId === condoId) : projects;
    return filteredProjects.filter(project => project.status === status);
  },

  getProjectsByYear: (year, condoId) => {
    const { projects } = get();
    const filteredProjects = condoId ? projects.filter(p => p.condoId === condoId) : projects;
    return filteredProjects.filter(project => {
      const startYear = new Date(project.startDate).getFullYear();
      const endYear = new Date(project.endDate).getFullYear();
      return startYear === year || endYear === year;
    });
  },

  getProjectsByCategory: (category, condoId) => {
    const { projects } = get();
    const filteredProjects = condoId ? projects.filter(p => p.condoId === condoId) : projects;
    return filteredProjects.filter(project => project.category === category);
  },

  getProjectSummary: (condoId) => {
    const { projects } = get();
    const filteredProjects = condoId ? projects.filter(p => p.condoId === condoId) : projects;

    const activeProjects = filteredProjects.filter(p =>
      p.status === 'IN_PROGRESS' || p.status === 'PLANNING'
    );
    const completedProjects = filteredProjects.filter(p => p.status === 'COMPLETED');
    const totalBudget = filteredProjects.reduce((sum, p) => sum + p.budget, 0);
    const totalActualCost = filteredProjects.reduce((sum, p) => sum + (p.actualCost || 0), 0);

    return {
      totalProjects: filteredProjects.length,
      activeProjects: activeProjects.length,
      completedProjects: completedProjects.length,
      totalBudget,
      totalActualCost,
      budgetVariance: totalBudget - totalActualCost
    };
  },

  // Expense Management with API integration
  fetchExpenses: async (condoId?: string) => {
    const token = nextRequestToken('expenses');
    set(loadStart('expenses')); // 106.1
    try {
      const expenses = await apiService.getExpenses(condoId);
      if (isStaleRequest(token)) return; // 94.4 — superseded by a newer fetch
      set(state => ({ ...loadSettle('expenses', null)(state), expenses }));
    } catch (error: any) {
      if (isStaleRequest(token)) return;
      set(loadSettle('expenses', error.message || 'Failed to fetch expenses'));
    }
  },

  addExpense: (expense) =>
    set((state) => ({
      expenses: [...state.expenses, { ...expense, date: new Date(expense.date) }]
    })),

  createExpense: async (expenseData) => {
    const { setLoading, setError, fetchExpenses } = get();
    setLoading(true);
    try {
      await apiService.createExpense(expenseData);
      await fetchExpenses(expenseData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create expense');
    } finally {
      setLoading(false);
    }
  },

  updateExpense: (id, updatedExpense) =>
    set((state) => ({
      expenses: state.expenses.map((expense) =>
        expense.id === id ? { ...expense, ...updatedExpense } : expense
      ),
    })),

  deleteExpense: (id) =>
    set((state) => ({
      expenses: state.expenses.filter((expense) => expense.id !== id),
    })),

  updateExpenseApi: async (id, expenseData) => {
    const { setLoading, setError, expenses, fetchExpenses } = get();
    setLoading(true);
    try {
      await apiService.updateExpense(id, expenseData);
      const existing = expenses.find(e => e.id === id);
      await fetchExpenses(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update expense');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteExpenseApi: async (id) => {
    const { setLoading, setError, expenses, fetchExpenses } = get();
    setLoading(true);
    try {
      const existing = expenses.find(e => e.id === id);
      await apiService.deleteExpense(id);
      await fetchExpenses(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete expense');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  getExpensesByPeriod: (startDate, endDate, condoId?: string) => {
    const { expenses } = get();
    const filteredExpenses = condoId ? expenses.filter(expense => expense.condoId === condoId) : expenses;
    return filteredExpenses.filter(expense =>
      dateUtils.isWithinInterval(new Date(expense.date), { start: startDate, end: endDate })
    );
  },

  // Revenue Management with API integration
  fetchRevenues: async (condoId?: string) => {
    const token = nextRequestToken('revenues');
    set(loadStart('revenues')); // 106.1
    try {
      const revenues = await apiService.getRevenues(condoId);
      if (isStaleRequest(token)) return; // 94.4 — superseded by a newer fetch
      set(state => ({ ...loadSettle('revenues', null)(state), revenues }));
    } catch (error: any) {
      if (isStaleRequest(token)) return;
      set(loadSettle('revenues', error.message || 'Failed to fetch revenues'));
    }
  },

  addRevenue: (revenue) =>
    set((state) => ({
      revenues: [...state.revenues, { ...revenue, date: new Date(revenue.date) }]
    })),

  createRevenue: async (revenueData) => {
    const { setLoading, setError, fetchRevenues } = get();
    setLoading(true);
    try {
      await apiService.createRevenue(revenueData);
      await fetchRevenues(revenueData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create revenue');
    } finally {
      setLoading(false);
    }
  },

  updateRevenue: (id, updatedRevenue) =>
    set((state) => ({
      revenues: state.revenues.map((revenue) =>
        revenue.id === id ? { ...revenue, ...updatedRevenue } : revenue
      ),
    })),

  deleteRevenue: (id) =>
    set((state) => ({
      revenues: state.revenues.filter((revenue) => revenue.id !== id),
    })),

  getRevenuesByPeriod: (startDate, endDate, condoId?: string) => {
    const { revenues } = get();
    const filteredRevenues = condoId ? revenues.filter(revenue => revenue.condoId === condoId) : revenues;
    return filteredRevenues.filter(revenue =>
      dateUtils.isWithinInterval(new Date(revenue.date), { start: startDate, end: endDate })
    );
  },
});
